import { GRASS_FRICTION, STOP_THRESHOLD } from './constants.js'
import type { Ball } from './ball.js'

/**
 * Apply grass rolling friction to a ball for one timestep.
 *
 * Model: constant deceleration along current velocity direction.
 * This matches experimental data for balls rolling on grass at
 * moderate speeds (Don Gugan, Rod Cross 2017).
 *
 * For future: sliding→rolling transition (Han 2005) when spin effects are added.
 *
 * @param ball  The ball to update (mutated in place)
 * @param dt    Timestep in seconds
 * @param friction  Deceleration in m/s² (defaults to GRASS_FRICTION)
 */
export function applyFriction(ball: Ball, dt: number, friction = GRASS_FRICTION): void {
  if (ball.state === 'stopped') return

  const speed = ball.speed
  if (speed <= STOP_THRESHOLD) {
    ball.stop()
    return
  }

  // Deceleration this frame: friction * dt, clamped so we don't reverse direction
  const dv = Math.min(friction * dt, speed)
  const scale = (speed - dv) / speed
  ball.vel.x *= scale
  ball.vel.y *= scale

  // Approximate angular velocity from rolling constraint: ω = v / r
  // For top-down view we track z-axis rotation (spin of ball viewed from above)
  ball.angularVel = ball.speed / ball.radius

  if (ball.speed <= STOP_THRESHOLD) {
    ball.stop()
  }
}
